import { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { IDataTimeChart } from '@ts/ui-models';
import MainCard from './MainCard';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/system';
import { Stack, Box, Skeleton } from '@mui/material';
import { IAppProps } from '@interfaces/App.interface';
import _ from 'lodash';
import { Grid, Typography } from '@ts/storybook';
import { useTranslation } from 'react-i18next';

interface TimeChartProps extends IAppProps {
  id: string;
  color?: string;
  title?: string;
  slot?: string;
  dataChart?: IDataTimeChart;
  loading: boolean;
  onChangeSlot?: (v: string) => void;
  isMock?: boolean;
}

//Chart options
const timeChartOptions: ApexOptions = {
  chart: {
    height: 470,
    type: 'rangeBar',
  },
  plotOptions: {
    bar: {
      horizontal: true,
      barHeight: '80%',
    },
  },
  xaxis: {
    type: 'datetime',
  },
  stroke: {
    width: 1,
  },
  fill: {
    type: 'solid',
    opacity: 0.6,
  },
  legend: {
    position: 'top',
    horizontalAlign: 'left',
  },
};

const mockActivityData = (slot: string | undefined): IDataTimeChart => {
  return {
    xaxis: {
      type: 'datetime',
    },
    series: [
      {
        name: 'Bob',
        data: [
          {
            x: 'Design',
            y: [new Date('2019-03-05').getTime(), new Date('2019-03-08').getTime()],
          },
          {
            x: 'Code',
            y: [new Date('2019-03-02').getTime(), new Date('2019-03-05').getTime()],
          },
          {
            x: 'Code',
            y: [new Date('2019-03-05').getTime(), new Date('2019-03-07').getTime()],
          },
          {
            x: 'Test',
            y: [new Date('2019-03-03').getTime(), new Date('2019-03-09').getTime()],
          },
          {
            x: 'Test',
            y: [new Date('2019-03-08').getTime(), new Date('2019-03-11').getTime()],
          },
          {
            x: 'Validation',
            y: [new Date('2019-03-11').getTime(), new Date('2019-03-16').getTime()],
          },
          {
            x: 'Design',
            y: [new Date('2019-03-01').getTime(), new Date('2019-03-03').getTime()],
          },
        ],
      },
      {
        name: 'Joe',
        data: [
          {
            x: 'Design',
            y: [new Date('2019-03-02').getTime(), new Date('2019-03-05').getTime()],
          },
          {
            x: 'Test',
            y: [new Date('2019-03-06').getTime(), new Date('2019-03-16').getTime()],
            goals: [
              {
                name: 'Break',
                value: new Date('2019-03-10').getTime(),
                strokeColor: '#CD2F2A',
              },
            ],
          },
          {
            x: 'Code',
            y: [new Date('2019-03-03').getTime(), new Date('2019-03-07').getTime()],
          },
          {
            x: 'Deployment',
            y: [new Date('2019-03-20').getTime(), new Date('2019-03-22').getTime()],
          },
          {
            x: 'Design',
            y: [new Date('2019-03-10').getTime(), new Date('2019-03-16').getTime()],
          },
        ],
      },
      {
        name: 'Dan',
        data: [
          {
            x: 'Code',
            y: [new Date('2019-03-10').getTime(), new Date('2019-03-17').getTime()],
          },
          {
            x: 'Validation',
            y: [new Date('2019-03-05').getTime(), new Date('2019-03-09').getTime()],
            goals: [
              {
                name: 'Break',
                value: new Date('2019-03-07').getTime(),
                strokeColor: '#CD2F2A',
              },
            ],
          },
        ],
      },
    ],
  };
};

/* const useStyles = makeStyles((theme: Theme) => ({
  positionRotatedText: { position: 'absolute', zIndex: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
  rotatedText: {
    color: theme.palette.text.disabled,
    textAlign: 'center',
    transform: 'rotate(-45deg)', // Elige el ángulo de rotación deseado (en grados)
  },
  noData: {
    backgroundColor: '#c4bfbf29',
  },
}));
 */
const TimeChart = (props: TimeChartProps) => {
  const { t } = useTranslation();

  const [state, setState] = useState(props.dataChart);

  //#endregion

  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState(props.isMock ? mockActivityData(props.slot).series : props.dataChart?.series || []);
  const [options, setOptions] = useState<ApexOptions>(timeChartOptions);
  const [slot, setSlot] = useState('week');

  //#region Handlers
  const onClickSlot = (type: string): any => {
    if (type !== slot && !loading) {
      setLoading(true);
      setSlot(type);
      props.onChangeSlot && props.onChangeSlot(type);
    }
  };
  //#endregion

  //#region Functions
  const fillOptionsChart = (prevState: any) => {
    if (props.id === '2time') {
      return {
        ...prevState,
        //colors: generateColorShades('#201b50', '#c5c1f7', series.length),

        theme: {
          mode: 'light',
          palette: 'palette2',
          monochrome: {
            enabled: false,
            color: '#255aee',
            shadeTo: 'light',
            shadeIntensity: 0.65,
          },
        },
      };
    } else {
      return {
        ...prevState,
        //colors: generateColorShades('#201b50', '#c5c1f7', series.length),

        theme: {
          mode: 'light',
          palette: 'palette7',
          monochrome: {
            enabled: false,
            color: '#255aee',
            shadeTo: 'light',
            shadeIntensity: 0.65,
          },
        },
      };
    }
  };
  const getData = () => {
    //Check if series is a list of objects
    if (Array.isArray(series) && typeof series[0] === 'number') {
      return series;
    } else if (Array.isArray(series) && typeof series[0] === 'object') {
      return series[0].data;
    }
  };
  const checkEmptyData = () => {
    if (props.isMock) return false;
    return !getData() || getData()?.length === 0;
  };
  //#endregion

  useEffect(() => {
    setOptions(prevState => fillOptionsChart(prevState));
    setSeries(props.isMock ? mockActivityData(props.slot).series : props.dataChart?.series || []);
    setLoading(false);
  }, [props.dataChart]);

  if (props.loading)
    return (
      <MainCard contentSX={{ p: 2.25 }} sx={{ mt: 2, height: '100%' }}>
        <Stack spacing={0.5}>
          <Grid container alignItems="center">
            <Grid size={{ xs: 7 }}>
              <Skeleton variant="rounded" height={32} />
            </Grid>
            <Grid size={{ xs: 1 }} />
            <Grid size={{ xs: 4 }}>
              <Skeleton variant="rounded" height={32} />
            </Grid>
          </Grid>
        </Stack>
        <Box sx={{ pt: 2.75 }}>
          <Skeleton variant="rounded" height={420} />
        </Box>
      </MainCard>
    );

  return (
    <MainCard contentSX={{ p: 2.25 }}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid mb={1.5}>{props.title && <Typography variant="h5" fontWeight="500" color="primary" text={props.title} />}</Grid>
      </Grid>
      <div className="overflow-auto relative">
        <div className="min-w-full min-h-[502px]">
          <ReactApexChart
            // @ts-ignore
            options={options}
            series={state?.series}
            // @ts-ignore
            type={timeChartOptions.chart?.type}
            height={timeChartOptions.chart?.height}
          />
        </div>
      </div>
    </MainCard>
  );
};

export default TimeChart;
